import { useState, type FormEvent } from "react";

import type { Department, LeaveType, Position } from "../../services/staffService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { DEPARTMENT_TYPES, POSITION_LEVELS } from "./hrSetupLogic";

/**
 * Create and edit forms for the three HR setup records. Each is one modal
 * whose Save sits in the footer; `record` switches it to editing, and
 * `onSave` hands back only the fields the form owns.
 */

interface FormProps<T, Data> {
  record?: T;
  busy: boolean;
  error?: string;
  onSave: (data: Data) => void;
  onClose: () => void;
}

// --- Department ----------------------------------------------------------------

export type DepartmentData = Pick<Department, "name" | "code" | "department_type" | "description">;

export function DepartmentForm({ record, busy, error, onSave, onClose }: FormProps<Department, DepartmentData>) {
  const [name, setName] = useState(record?.name ?? "");
  const [type, setType] = useState<Department["department_type"]>(record?.department_type ?? "academic");
  const [description, setDescription] = useState(record?.description ?? "");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({
      name: name.trim(),
      code: String(data.get("code")).trim().toUpperCase(),
      department_type: type,
      description: description.trim(),
    });
  };
  return (
    <FormModal
      id="department-form"
      title={record ? `Edit ${record.name}` : "New department"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create department"}
      note={record ? "The head changes from the department's details, with a reason." : "A new department starts active."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Department" description="How it appears on staff profiles and reports." divided={false} columns={2}>
        <Field label="Name">
          <input required autoFocus name="name" value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Sciences" />
        </Field>
        <Field label="Short code" hint="Unique; letters and numbers, shown in capitals.">
          <input required name="code" maxLength={20} defaultValue={record?.code} className="input mt-1 uppercase" placeholder="e.g. SCI" />
        </Field>
        <Field label="Type">
          <select required name="department_type" value={type} onChange={(event) => setType(event.target.value as Department["department_type"])} className="input mt-1">
            {DEPARTMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="Responsibilities" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            name="description"
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What this department is responsible for"
            context={{ key: "staff.setup_description", params: { kind: "department", id: record?.id, name, department_type: type } }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the name, the type and the other departments." : "Name the department first, then draft."}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

// --- Position --------------------------------------------------------------------

export type PositionData = Pick<
  Position,
  "title" | "department" | "level" | "experience_required" | "salary_range_min" | "salary_range_max" | "description" | "qualifications"
>;

export function PositionForm({
  record,
  departments,
  busy,
  error,
  onSave,
  onClose,
}: FormProps<Position, PositionData> & { departments: Department[] }) {
  const [title, setTitle] = useState(record?.title ?? "");
  const [department, setDepartment] = useState(record?.department ? String(record.department) : "");
  const [level, setLevel] = useState(record?.level ?? "entry");
  const [experience, setExperience] = useState(String(record?.experience_required ?? 0));
  const [description, setDescription] = useState(record?.description ?? "");
  const [minimum, setMinimum] = useState(record?.salary_range_min ? String(Number(record.salary_range_min)) : "");
  const [maximum, setMaximum] = useState(record?.salary_range_max ? String(Number(record.salary_range_max)) : "");
  const rangeWrong = Boolean(minimum && maximum && Number(minimum) > Number(maximum));
  // Inactive departments stay listed only when this position is already in one.
  const choices = departments.filter((item) => item.is_active || String(item.id) === department);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (rangeWrong) return;
    const data = new FormData(event.currentTarget);
    onSave({
      title: title.trim(),
      department: Number(department) || null,
      level,
      experience_required: Number(experience) || 0,
      // null clears a range that was set.
      salary_range_min: minimum || null,
      salary_range_max: maximum || null,
      description: description.trim(),
      qualifications: String(data.get("qualifications") ?? "").trim(),
    });
  };
  return (
    <FormModal
      id="position-form"
      title={record ? `Edit ${record.title}` : "New position"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create position"}
      note="Positions become choices on staff profiles."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Position" divided={false} columns={2}>
        <Field label="Job title">
          <input required autoFocus value={title} onChange={(event) => setTitle(event.target.value)} className="input mt-1" placeholder="e.g. Senior Teacher" />
        </Field>
        <Field label="Department" hint="Leave as school-wide for a post outside any one department.">
          <select value={department} onChange={(event) => setDepartment(event.target.value)} className="input mt-1">
            <option value="">School-wide</option>
            {choices.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_active ? "" : " (inactive)"}</option>)}
          </select>
        </Field>
        <Field label="Level">
          <select value={level} onChange={(event) => setLevel(event.target.value)} className="input mt-1">
            {POSITION_LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Experience required" hint="In years; 0 if none.">
          <input type="number" min={0} value={experience} onChange={(event) => setExperience(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="Pay range" description="A guide for pay changes, in UGX a month. It does not set anyone's salary." columns={2}>
        <Field label="Minimum" optional>
          <input type="number" min={0} step="any" value={minimum} onChange={(event) => setMinimum(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Maximum" optional hint={rangeWrong ? <span className="text-red-700">The maximum is below the minimum.</span> : undefined}>
          <input type="number" min={0} step="any" value={maximum} onChange={(event) => setMaximum(event.target.value)} aria-invalid={rangeWrong} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="Role and requirements" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            name="description"
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What the holder does"
            context={{
              key: "staff.setup_description",
              params: { kind: "position", id: record?.id, name: title, department: department || null, level, experience_required: experience },
            }}
            draftLabel="Draft description"
            draftHint={title.trim() ? "Uses the title, level, department and the positions beside it." : "Give the job title first, then draft."}
          />
        </Field>
        <Field label="Qualifications" optional hint="One per line, e.g. Bachelor of Education; registered with the teaching service.">
          <textarea name="qualifications" rows={3} defaultValue={record?.qualifications ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

// --- Leave policy ------------------------------------------------------------------

export type LeaveData = Pick<LeaveType, "name" | "code" | "max_days_per_year" | "is_paid" | "requires_approval" | "description">;

export function LeavePolicyForm({ record, busy, error, onSave, onClose }: FormProps<LeaveType, LeaveData>) {
  const [name, setName] = useState(record?.name ?? "");
  const [days, setDays] = useState(record ? String(record.max_days_per_year) : "");
  const [paid, setPaid] = useState(record?.is_paid ?? true);
  const [approval, setApproval] = useState(record?.requires_approval ?? true);
  const [description, setDescription] = useState(record?.description ?? "");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({
      name: name.trim(),
      code: String(data.get("code")).trim().toUpperCase(),
      max_days_per_year: Number(days),
      is_paid: paid,
      requires_approval: approval,
      description: description.trim(),
    });
  };
  return (
    <FormModal
      id="leave-form"
      title={record ? `Edit ${record.name}` : "New leave policy"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create leave policy"}
      note={record && (record.in_use_by?.["leave requests"] ?? 0) > 0 ? "Changes apply to new requests; leave already taken is not recalculated." : "Staff choose from active policies when they request leave."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Policy" divided={false} columns={3}>
        <Field label="Name">
          <input required autoFocus value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Annual leave" />
        </Field>
        <Field label="Short code" hint="Unique, shown in capitals.">
          <input required name="code" maxLength={20} defaultValue={record?.code} className="input mt-1 uppercase" placeholder="e.g. ANNUAL" />
        </Field>
        <Field label="Days a year">
          <input required type="number" min={1} value={days} onChange={(event) => setDays(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="Rules" columns={2}>
        {([
          [paid, setPaid, "Paid leave", "Salary continues while staff are away."],
          [approval, setApproval, "Needs approval", "A manager approves each request before it is taken."],
        ] as const).map(([checked, set, label, help]) => (
          <label key={label} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
            <input type="checkbox" checked={checked} onChange={(event) => set(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-primary-600" />
            <span>
              <span className="block font-medium text-gray-900">{label}</span>
              <span className="block text-xs text-gray-500">{help}</span>
            </span>
          </label>
        ))}
      </FormSection>
      <FormSection title="When it applies" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            name="description"
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="When staff should use this policy"
            context={{
              key: "staff.setup_description",
              params: { kind: "leave_policy", id: record?.id, name, max_days_per_year: days, is_paid: String(paid), requires_approval: String(approval) },
            }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the name, allowance and rules above." : "Name the policy first, then draft."}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
